import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";

import {
  ArrowLeft,
  CheckCircle,
  Gift,
} from "lucide-react";

import Navbar from "../components/NavbarM";

import {
  awardPoints,
  getMemberAtStore,
} from "../services/memberService";

import {
  calculateEarnedPoints,
  getActivePointsRate,
  isPromotionCurrentlyActive,
  loadRewardSettings,
} from "../services/rewardSettingsService";

function AddPointsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { memberId } = useParams();

  const lookupMethod =
    location.state?.lookupMethod || "phone";

  const [member, setMember] = useState(null);
  const [membership, setMembership] = useState(null);
  const [rewardSettings, setRewardSettings] =
    useState(null);

  const [totalAmount, setTotalAmount] = useState("");
  const [eligibleAmount, setEligibleAmount] =
    useState("");
  const [eligibleAmountEdited, setEligibleAmountEdited] =
    useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successResult, setSuccessResult] =
    useState(null);

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      setError("");

      try {
        // Both calls run server-side (Cloud Functions) since members/
        // storeMemberships/settings aren't directly readable by clients.
        const [customerResult, settingsResult] =
          await Promise.all([
            getMemberAtStore(memberId),
            loadRewardSettings(),
          ]);

        setMember(customerResult.member);
        setMembership(customerResult.membership);
        setRewardSettings(settingsResult);
      } catch (loadError) {
        console.error(loadError);

        setError(
          loadError.message ||
            "The customer information could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }

    loadPage();
  }, [memberId]);

  const activePointsRate = useMemo(() => {
    if (!rewardSettings) return 10;

    return getActivePointsRate(rewardSettings);
  }, [rewardSettings]);

  const promotionActive = useMemo(() => {
    if (!rewardSettings) return false;

    return isPromotionCurrentlyActive(
      rewardSettings
    );
  }, [rewardSettings]);

  const previewPoints = useMemo(() => {
    if (!eligibleAmount) return 0;

    try {
      return calculateEarnedPoints(
        eligibleAmount,
        activePointsRate
      );
    } catch {
      return 0;
    }
  }, [eligibleAmount, activePointsRate]);

  const excludedAmount = useMemo(() => {
    const total = Number(totalAmount || 0);
    const eligible = Number(eligibleAmount || 0);

    return Math.max(0, total - eligible);
  }, [totalAmount, eligibleAmount]);

  function handleTotalAmountChange(event) {
    const value = event.target.value;

    setTotalAmount(value);

    if (!eligibleAmountEdited) {
      setEligibleAmount(value);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setSuccessResult(null);
    setSaving(true);

    try {
      // storeId/storeName/employeeUid are no longer sent from the client
      // — the storeAwardPoints Cloud Function derives them from the
      // signed-in store's own record, so an employee can't award points
      // to a different store by spoofing these fields.
      const result = await awardPoints({
        memberId,
        totalAmount,
        eligibleAmount,
        lookupMethod,
      });

      setSuccessResult(result);

      setMembership((currentMembership) => ({
        ...currentMembership,
        points: result.pointsAfter,
      }));

      setTotalAmount("");
      setEligibleAmount("");
      setEligibleAmountEdited(false);

      setTimeout(() => {
        navigate("/Home", { replace: true });
      }, 1800);
    } catch (saveError) {
      console.error(saveError);

      setError(
        saveError.message ||
          "The points could not be added."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <Navbar />

        <Container className="py-5 text-center">
          <Spinner animation="border" />
          <p className="text-muted mt-3">
            Loading customer account
          </p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Container
        className="py-4"
        style={{ maxWidth: "850px" }}
      >
        <Button
          variant="link"
          className="px-0 mb-3 text-decoration-none"
          onClick={() =>
            navigate(
              lookupMethod === "scan"
                ? "/pages/scan-customer"
                : "/pages/find-customer"
            )
          }
        >
          <ArrowLeft size={18} className="me-2" />
          {lookupMethod === "scan" ? "Back to Scan" : "Back to Customer Search"}
        </Button>

        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {successResult && (
          <Alert
            variant="success"
            className="d-flex align-items-start gap-3"
          >
            <CheckCircle
              size={24}
              className="flex-shrink-0 mt-1"
            />

            <div>
              <strong>
                Points added successfully
              </strong>

              <div className="mt-1">
                {successResult.pointsEarned.toLocaleString()}{" "}
                points were added.
              </div>

              <div>
                New balance:{" "}
                {successResult.pointsAfter.toLocaleString()}{" "}
                points
              </div>
            </div>
          </Alert>
        )}

        {member && membership && (
          <>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                  <div>
                    <h2 className="mb-2">
                      {member.name}
                    </h2>

                    <p className="text-muted mb-1">
                      Phone ending in{" "}
                      {member.phoneLastFour}
                    </p>

                    <p className="mb-0">
                      Current balance:{" "}
                      <strong>
                        {Number(
                          membership.points || 0
                        ).toLocaleString()}{" "}
                        points
                      </strong>
                    </p>
                  </div>

                  <div className="text-end">
                    {member.isPhoneVerified ? (
                      <Badge bg="success">
                        Verified Customer
                      </Badge>
                    ) : (
                      <Badge bg="secondary">
                        Verification Pending
                      </Badge>
                    )}

                    <div className="mt-2">
                      <Badge
                        bg={
                          promotionActive
                            ? "warning"
                            : "primary"
                        }
                        text={
                          promotionActive
                            ? "dark"
                            : undefined
                        }
                      >
                        {activePointsRate} points per $1
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4 p-md-5">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3"
                    style={{
                      width: 52,
                      height: 52,
                      background: "#fff3e8",
                      color: "#f97316",
                    }}
                  >
                    <Gift size={26} />
                  </div>

                  <div>
                    <h3 className="mb-1">
                      Add Purchase Points
                    </h3>

                    <p className="text-muted mb-0">
                      Enter the full total and the amount
                      eligible for rewards.
                    </p>
                  </div>
                </div>

                {lookupMethod === "phone" && (
                  <Alert variant="light" className="border">
                    This customer was found using their
                    phone number. You can add points, but
                    rewards cannot be redeemed without
                    scanning their PointsHub code.
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Total purchase
                        </Form.Label>

                        <Form.Control
                          type="number"
                          size="lg"
                          min="0"
                          step="0.01"
                          value={totalAmount}
                          onChange={
                            handleTotalAmountChange
                          }
                          placeholder="0.00"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Eligible amount
                        </Form.Label>

                        <Form.Control
                          type="number"
                          size="lg"
                          min="0"
                          step="0.01"
                          value={eligibleAmount}
                          onChange={(event) => {
                            setEligibleAmount(
                              event.target.value
                            );

                            setEligibleAmountEdited(true);
                          }}
                          placeholder="0.00"
                          required
                        />

                        <Form.Text>
                          Exclude lottery, tobacco, gift
                          cards, taxes or other restricted
                          purchases.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Card className="bg-light border-0 my-4">
                    <Card.Body>
                      <Row className="g-3">
                        <Col sm={4}>
                          <div className="text-muted small">
                            Excluded amount
                          </div>

                          <strong>
                            ${excludedAmount.toFixed(2)}
                          </strong>
                        </Col>

                        <Col sm={4}>
                          <div className="text-muted small">
                            Points rate
                          </div>

                          <strong>
                            {activePointsRate} points/$
                          </strong>
                        </Col>

                        <Col sm={4}>
                          <div className="text-muted small">
                            Points to add
                          </div>

                          <strong className="fs-5">
                            {previewPoints.toLocaleString()}
                          </strong>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-100"
                    disabled={
                      saving || previewPoints <= 0
                    }
                  >
                    {saving ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Adding Points
                      </>
                    ) : (
                      <>
                        <Gift
                          size={19}
                          className="me-2"
                        />
                        Confirm and Add{" "}
                        {previewPoints.toLocaleString()}{" "}
                        Points
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </>
  );
}

export default AddPointsPage;
